import React, { useEffect } from "react";
import { MantineProvider } from "@mantine/core";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";

// core styles are required for all packages
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";

import "./index.scss";

import LoginPage from "./pages/Login";
import RequireAuth from "./components/RequireAuth";
import TipsPage from "./pages/_Protected/Tips";
import Dashboard from "./pages/_Protected/Dashboard";
import Overview from "./pages/_Protected/Overview";
import Analytics from "./pages/_Protected/Analytics";
import Settings from "./pages/_Protected/Settings/index";
// Deleted pages imports removed

import Testimonials from "./pages/_Protected/Testimonials";
import Profile from "./pages/_Protected/Profile";
import Newsletter from "./pages/_Protected/Newsletter";
import Emails from "./pages/_Protected/Emails";
import NewEmailPage from "./pages/_Protected/Emails/New";
import UwipMembers from "./pages/_Protected/UwipMembers";
import NotFound from "./pages/NotFound";
import { usePerformRequest } from "./lib/usePerformRequest";
import { NonPaginatedResponse } from "./lib/types";
import { IAdminProps } from "./lib/types/Admin";
import { Endpoints } from "./lib/endpoints";
import { HttpCodes } from "./lib/data";

// other css files are required only if
// you are using components from the corresponding package
// import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
// import '@mantine/code-highlight/styles.css';
// ...

function RedirectWhereNecessary() {
  const token = localStorage.getItem("t");

  const { response } = usePerformRequest<NonPaginatedResponse<IAdminProps>>({
    method: Endpoints.GetProfile().method,
    route: Endpoints.GetProfile().route,
  });

  useEffect(() => {
    if (response && response.status) {
      if (response && response.status === HttpCodes.Success) {
        window.location.href = "/d";
      } else if (response && response.status === HttpCodes.Unauthorized) {
        window.location.href = "/login";
      } else {
        window.location.href = "/login";
      }
    }
  }, [response]);
}

const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/d" replace /> },
  { path: "/login", element: <LoginPage /> },
  {
    path: "/d",
    element: (
      <RequireAuth>
        <Dashboard />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Overview /> },
      { path: "profile", element: <Profile /> },
      { path: "analytics", element: <Analytics /> },
      { path: "settings", element: <Settings /> },
      { path: "tips", element: <TipsPage /> },
      { path: "testimonials", element: <Testimonials /> },
      { path: "newsletter", element: <Newsletter /> },
      { path: "uwip", element: <UwipMembers /> },
      { path: "emails", element: <Emails /> },
      { path: "emails/new", element: <NewEmailPage /> },
    ],
  },
  // Keep old route for backwards compatibility
  {
    path: "/tips",
    element: (
      <RequireAuth>
        <TipsPage />
      </RequireAuth>
    ),
  },
  // 404 Not Found - must be last
  {
    path: "*",
    element: <NotFound />,
  },
]);

export default function App() {
  return (
    <MantineProvider
      defaultColorScheme="light"
      theme={{
        defaultRadius: "xs",
        components: {
          Modal: {
            defaultProps: { centered: true },
          },
          Paper: {
            defaultProps: { shadow: "xs", radius: "xs" },
          },
          Button: {
            defaultProps: { radius: "xs" },
          },
          TextInput: {
            defaultProps: { radius: "xs" },
          },
        },
      }}
    >
      <RouterProvider router={router} />
    </MantineProvider>
  );
}
